// src/components/dash/section/publishPost.js
//
// Mechanical extraction of PostCreator.vue's sendPostAllPost() switch(this.mode) branches - moved
// here unchanged (parameter-passing refactor only, no behavior change) so the SAME business logic
// can be called from two places: the live/fast path (sendPostAllPost, jobIds empty - runs exactly
// like before) and the deferred-publish path (a submit that had to wait for a still-running
// mediaEditor export - see PostCreator.vue's sendPostAllPost/mediaExportJobs.js's
// registerPendingPublish). There is only ONE copy of this logic - never call the old inline switch
// body, it no longer exists.
//
// Every branch takes:
//   - `snapshot`: a plain object with the same fields the original code read off `this`
//     (dataFormRequest, mode, dataEditPost, draft, draftNews, saveAllFormData, idUsersAssigned,
//     requiredValidation, pagesSelected, allPages, currentUser, logs, files - not every branch
//     reads every field, see each one below).
//   - `deps`: bound instance methods the branches call, captured via `this.x.bind(this)` by the
//     caller (confirmed portable - createAllPosts/editAllPosts/generateLog/addLog/deleteDraft/events
//     only ever depend on `this.$http`/`this.$store`/plain params, never on other component state -
//     see mixins/Posts.vue, mixins/Drafts.vue, mixins/Common.vue, postCreator/mixins/main.js).
//     Expected shape: { createAllPosts, editAllPosts, generateLog, addLog, deleteDraft, events, t }
//     (`t` is `this.$t` bound to the calling component - i18n has no standalone non-component
//     access pattern anywhere else in this codebase, see PostCreator.vue's sendPostAllPost for how
//     it's captured before a deferred publish's component instance may get destroyed by navigation).
//
// EventBus/findDifferences/mergeUpdates/editableNetworks are plain module-level imports (not
// component state), so they're just imported directly here like everywhere else in this codebase.
import { EventBus } from '@/event-bus'
import { findDifferences, mergeUpdates } from './../postCreator/utils.js'
import { editableNetworks } from './../postCreator/constante.js'
import { clarityEvent } from '@/utils/clarity'

// Default: exactly today's behavior (one EventBus.$emit spawning fresh RequestManager rows for the
// whole group). The deferred-publish path (PostCreator.vue's sendPostAllPost) overrides this via
// deps.publishRequestGroup so each page's placeholder row (media-prep phase, see
// sendPostAllPostDeferred) can pick up its OWN real per-page tracking here instead of a brand new
// row appearing - the immediate/fast path never sets this override, so it's unaffected.
const defaultPublishRequestGroup = (requestGroup, cbFinish) => {
    EventBus.$emit('requestManagerAddGroup', requestGroup, cbFinish)
}

export function publishCreateOrDuplicate(snapshot, deps) {
    const { dataFormRequest, currentUser, pagesSelected, allPages, mode } = snapshot
    const { createAllPosts, generateLog, addLog, events, t } = deps
    const requestGroup = []

    const { draft, front_version } = Object.values(dataFormRequest).find((dfr) => !dfr.hasOwnProperty('error')) || {}

    const log = generateLog({
        title: 'dash.posts.log.created-posts',
    })

    for (const [pageId, formData] of Object.entries(dataFormRequest)) {
        if (formData.hasOwnProperty('error')) {
            delete dataFormRequest[pageId];
        } else {
            formData.log = JSON.stringify([JSON.stringify(log)]);
        }
    }

    return createAllPosts(dataFormRequest).then((response) => {
        addLog(currentUser, `${mode}All-post`, {
            payload: {
                ...dataFormRequest,
                draft,
                front_version,
            },
            response: response
        })

        Object.entries(response.body.news).forEach(([pageId, news]) => {
            console.log("CREATEALL post loop", response.body.news)
            const page = pagesSelected.find(page => page.id == pageId) || allPages[pageId]

            requestGroup.push({
                pageId,
                start: async (onSuccess, onprogress, onError) => {
                    if (news.scheduled_date) {
                        onSuccess(t('dash.post-creator.request.post-scheduled'));
                        EventBus.$emit('planner/refresh', 500);
                        return;
                    }
                    try {
                        console.log("creatall eventsource")
                        const eventSource = events(news.id);

                        eventSource.onopen = () => {
                            console.log('createAll event opened: ', news.id);
                        }
                        eventSource.onerror = (error) => {
                            console.log('createAll event onerror', error, "postId: ", news.id);
                            onError(t('dash.post-creator.request.post-publish-failed'));
                            eventSource.close();
                        }
                        eventSource.onmessage = (event) => {
                            console.log('createAll onmessage (', news, ') : ', event)
                            if (event.data === 'keep alive') return;
                            const data = JSON.parse(event.data);
                            if (data.progress.aborted) return;
                            if (data.progress.success) {
                                eventSource.close();
                                onSuccess(t('dash.post-creator.request.post-published'));
                                EventBus.$emit('planner/refresh', 500)
                            } else if (data.progress.success === false) {
                                eventSource.close();
                                onError(t('dash.post-creator.request.post-publish-failed'));
                                EventBus.$emit('planner/refresh', 500)
                            } else {
                                onprogress(data.progress.message);
                            }
                        }

                    } catch (error) {
                        onError(error.message)
                        return { response: null, news: { ...response.body.news, pageId } }
                    }
                },
                params: {
                    picture: news.enclosure || page.avatar,
                    title: `<span class="pink">${page.name.charAt(0).toUpperCase() + page.name.slice(1)}</span>: ${news.title || "post medias"}`,
                    loadingText: !news.scheduled_date ? t('dash.post-creator.request.publishing') : t('dash.post-creator.request.scheduling')
                }
            })
        });
        (deps.publishRequestGroup || defaultPublishRequestGroup)(requestGroup, async (responses) => { EventBus.$emit('planner/refresh', 0) });
    })
        .catch((err) => {
            console.log("createAllPosts error: ", err)
        })
}

export function publishEdit(snapshot, deps) {
    const { dataFormRequest, currentUser, saveAllFormData, dataEditPost, idUsersAssigned, requiredValidation, pagesSelected, allPages, mode } = snapshot
    const { editAllPosts, generateLog, addLog, events, t } = deps
    const requestGroup = []

    const { draft, front_version } = Object.values(dataFormRequest).find((dfr) => !dfr.hasOwnProperty('error')) || {}

    const log = generateLog({
        title: 'dash.posts.log.edited-posts',
        newFormData: saveAllFormData,
        lastFormData: dataEditPost.draft.saveAllFormData,
        newIdUsersAssigned: idUsersAssigned,
        lastIdUsersAssigned: dataEditPost.draft.idUsersAssigned,
        newRequiredValidation: requiredValidation,
        lastRequiredValidation: dataEditPost.draft.requiredValidation
    });

    // Add existing newsId in data
    for (const [pageId, formData] of Object.entries(dataFormRequest)) {
        if (formData.hasOwnProperty('error')) {
            delete dataFormRequest[pageId];
        } else {
            const post = dataEditPost.posts.find(post => post.page == pageId)

            if (post) {
                //update post for page
                formData.postId = post.postId
                formData.id = post._id
                formData.log = JSON.stringify([JSON.stringify(log)])
            }
        }
    }

    return editAllPosts(dataFormRequest).then((response) => {
        addLog(currentUser, `${mode}All-post`, {
            payload: {
                ...dataFormRequest,
                draft,
                front_version,
            },
            response: response
        });
        Object.entries(response.body.news).forEach(([pageId, news]) => {
            console.log("EDITALL post loop", response.body.news)
            const page = pagesSelected.find(page => page.id == pageId) || allPages[pageId]
            const formFromPageId = dataFormRequest[pageId];
            requestGroup.push({
                pageId,
                start: async (onSuccess, onprogress, onError) => {
                    if (formFromPageId.scheduled) {
                        onSuccess(t('dash.post-creator.request.changes-visible'));
                        EventBus.$emit('planner/refresh', 500);
                        return;
                    }
                    try {
                        console.log("editAll eventsource")
                        const eventSource = events(news.id);

                        eventSource.onopen = () => {
                            console.log('editAll event opened: ', news.id);
                        }
                        eventSource.onerror = (error) => {
                            console.log('editAll event onerror', error, "postId: ", news.id);
                            onError(t('dash.post-creator.request.post-publish-failed'));
                            eventSource.close();
                        }
                        eventSource.onmessage = (event) => {
                            console.log('editAll onmessage (', news, ') : ', event)
                            if (event.data === 'keep alive') return;
                            const data = JSON.parse(event.data);
                            if (data.progress.aborted) return;
                            if (data.progress.success) {
                                eventSource.close();
                                onSuccess(t('dash.post-creator.request.post-published'));
                                EventBus.$emit('planner/refresh', 500)
                            } else if (data.progress.success === false) {
                                eventSource.close();
                                onError(t('dash.post-creator.request.post-publish-failed'));
                                EventBus.$emit('planner/refresh', 500)
                            } else {
                                onprogress(data.progress.message);
                            }
                        }

                    } catch (error) {
                        onError(error.message)
                        return { response: null, news: { ...response.body.news, pageId } }
                    }
                },
                params: {
                    picture: formFromPageId.data.enclosure || page.avatar,
                    title: `<span class="pink">${page.name.charAt(0).toUpperCase() + page.name.slice(1)}</span>: ${formFromPageId.data.title || "post medias"}`,
                    loadingText: !formFromPageId.data.scheduled_date ? t('dash.post-creator.request.publishing') : t('dash.post-creator.request.scheduling')
                }
            })
        });
        (deps.publishRequestGroup || defaultPublishRequestGroup)(requestGroup, async (responses) => { EventBus.$emit('planner/refresh', 0) });
    })
        .catch((err) => {
            console.log("editAllPosts error: ", err)
        })
}

export function publishDraft(snapshot, deps) {
    const { dataFormRequest, saveAllFormData, draft: pgDraft, draftNews, logs, currentUser, pagesSelected, allPages, mode, idUsersAssigned, requiredValidation } = snapshot
    const { createAllPosts, editAllPosts, generateLog, addLog, events, t } = deps
    const requestGroup = []

    const { draft, front_version } = Object.values(dataFormRequest).find((dfr) => !dfr.hasOwnProperty('error')) || {}

    let log = [
        JSON.stringify(generateLog({
            title: 'dash.posts.log.scheduled-draft'
        }))
    ]

    const logEdited = generateLog({
        title: 'dash.posts.log.edited-posts',
        newFormData: saveAllFormData,
        lastFormData: pgDraft.data?.saveAllFormData,
        newIdUsersAssigned: idUsersAssigned,
        lastIdUsersAssigned: pgDraft.data?.idUsersAssigned,
        newRequiredValidation: requiredValidation,
        lastRequiredValidation: pgDraft.data?.requiredValidation
    })

    if (logEdited.content) {
        logEdited.created_at = new Date((new Date(logEdited.created_at)).getTime() + 1000).toISOString()
        log.push(JSON.stringify(logEdited))
    }

    // Brouillon PG converti en MongoDB via saveDraft → publier via editAll
    if (draftNews) {
        for (const [pageId, formData] of Object.entries(dataFormRequest)) {
            if (formData.hasOwnProperty('error')) {
                delete dataFormRequest[pageId];
            } else {
                formData.id = draftNews[pageId]
                formData.log = JSON.stringify([...log, ...logs]);
            }
        }
        return editAllPosts(dataFormRequest).then((response) => {
            Object.entries(response.body.news).forEach(([pageId, news]) => {
                const page = pagesSelected.find(p => p.id == pageId) || allPages[pageId]
                const formFromPageId = dataFormRequest[pageId]
                requestGroup.push({
                    pageId,
                    start: async (onSuccess, onprogress, onError) => {
                        if (formFromPageId?.scheduled) {
                            onSuccess(t('dash.post-creator.request.post-scheduled'));
                            EventBus.$emit('planner/refresh', 500);
                            return;
                        }
                        try {
                            const eventSource = events(news.id);
                            eventSource.onerror = () => { onError(t('dash.post-creator.request.post-publish-failed')); eventSource.close(); }
                            eventSource.onmessage = (event) => {
                                if (event.data === 'keep alive') return;
                                const data = JSON.parse(event.data);
                                if (data.progress.success) { eventSource.close(); onSuccess(t('dash.post-creator.request.post-published')); EventBus.$emit('planner/refresh', 500) }
                                else if (data.progress.success === false) { eventSource.close(); onError(t('dash.post-creator.request.post-publish-failed')); EventBus.$emit('planner/refresh', 500) }
                                else onprogress(data.progress.message)
                            }
                        } catch (error) { onError(error.message) }
                    },
                    params: {
                        picture: formFromPageId?.data.enclosure || page?.avatar,
                        title: `<span class="pink">${page?.name?.charAt(0).toUpperCase() + page?.name?.slice(1)}</span>: ${formFromPageId?.data.title || 'post medias'}`,
                        loadingText: !formFromPageId?.data.scheduled_date ? t('dash.post-creator.request.publishing') : t('dash.post-creator.request.scheduling')
                    }
                })
            });
            (deps.publishRequestGroup || defaultPublishRequestGroup)(requestGroup, async () => { EventBus.$emit('planner/refresh', 0) });
        }).catch(err => console.log("editAllPosts (draft→publish) error:", err))
    }

    // Brouillon PG original → comportement inchangé
    for (const [pageId, formData] of Object.entries(dataFormRequest)) {
        if (formData.hasOwnProperty('error')) {
            delete dataFormRequest[pageId];
        } else {
            formData.log = JSON.stringify([...log, ...logs]);
            formData.data.removeDraft = pgDraft.id;
        }
    }

    return createAllPosts(dataFormRequest).then((response) => {
        addLog(currentUser, `${mode}All-post`, {
            payload: {
                ...dataFormRequest,
                draft,
                front_version,
            },
            response: response
        })
        if (response.body.failedToRemoveDraft) {
            try {
                deps.deleteDraft(pgDraft.id)
            } catch (e) {
                console.error('Fallback draft deletion failed', e)
            }
        }

        Object.entries(response.body.news).forEach(([pageId, news]) => {
            console.log("CREATEALL from draft post loop", response.body.news)
            const page = pagesSelected.find(page => page.id == pageId) || allPages[pageId]
            requestGroup.push({
                pageId,
                start: async (onSuccess, onprogress, onError) => {
                    if (news.scheduled_date) {
                        onSuccess(t('dash.post-creator.request.post-scheduled'));
                        EventBus.$emit('planner/refresh', 500);
                        return;
                    }
                    try {
                        console.log("creatall eventsource")
                        const eventSource = events(news.id);

                        eventSource.onopen = () => {
                            console.log('createAll event opened: ', news.id);
                        }
                        eventSource.onerror = (error) => {
                            console.log('createAll event onerror', error, "postId: ", news.id);
                            onError(t('dash.post-creator.request.post-publish-failed'));
                            eventSource.close();
                        }
                        eventSource.onmessage = (event) => {
                            console.log('createAll onmessage (', news, ') : ', event)
                            if (event.data === 'keep alive') return;
                            const data = JSON.parse(event.data);
                            if (data.progress.aborted) return;
                            if (data.progress.success) {
                                eventSource.close();
                                onSuccess(t('dash.post-creator.request.post-published'));
                                EventBus.$emit('planner/refresh', 500);
                            } else if (data.progress.success === false) {
                                eventSource.close();
                                onError(t('dash.post-creator.request.post-publish-failed'));
                                EventBus.$emit('planner/refresh', 500);
                            } else {
                                onprogress(data.progress.message);
                            }
                        }

                    } catch (error) {
                        onError(error.message)
                        return { response: null, news: { ...response.body.news, pageId } }
                    }
                },
                params: {
                    picture: news.enclosure || page.avatar,
                    title: `<span class="pink">${page.name.charAt(0).toUpperCase() + page.name.slice(1)}</span>: ${news.title || "post medias"}`,
                    loadingText: !news.scheduled_date ? t('dash.post-creator.request.publishing') : t('dash.post-creator.request.scheduling')
                }
            })
        });
        (deps.publishRequestGroup || defaultPublishRequestGroup)(requestGroup, async (responses) => { EventBus.$emit('planner/refresh', 0) });
    })
        .catch((err) => {
            console.log("createAllPosts error: ", err)
        })
}

export function publishUpdate(snapshot, deps) {
    const { dataFormRequest, saveAllFormData, dataEditPost, currentUser, pagesSelected, allPages, files, mode } = snapshot
    const { editAllPosts, generateLog, addLog, events, t } = deps
    const requestGroup = []

    const { draft, front_version } = Object.values(dataFormRequest).find((dfr) => !dfr.hasOwnProperty('error')) || {}

    // checker les reseaux failed a l'edition

    //ajouter personalisation si toutes les pages ne sont pas editable
        //update mettre dans le custom network

    // boulcer sur les form
         // si
    let newDraft = null

    const pagesIdFailed = Object.entries(dataFormRequest).reduce((acc, [pageId, formData]) => {
        if (formData.hasOwnProperty('error')) acc.push(pageId)
        return acc
    }, []);


    //ajouter personalisation si toutes les pages ne sont pas editable ou error sur une page
    if (
        dataEditPost.posts.some(post => !allPages[post.page] || !editableNetworks.includes(allPages[post.page].network)) ||
        pagesIdFailed.length
    ) {

        const networkFailed = pagesIdFailed.reduce((acc, pageId) => {
            if (allPages[pageId] && !acc.includes(allPages[pageId].network)) acc.push(allPages[pageId].network)
            return acc
        }, []);

        const update = findDifferences(dataEditPost.draft.saveAllFormData.main, saveAllFormData.main)
        const originalDraft = JSON.parse(JSON.stringify(dataEditPost.draft))

        for (const [pageId, formData] of Object.entries(dataFormRequest)) {
            const page = pagesSelected.find(page => page.id == pageId) || allPages[pageId]
            if (!formData.hasOwnProperty('error')) {
                if (!networkFailed.includes(page.network)) {
                    //pas d'erreur de page ni sur le reseeau
                    //alors mettre l'update dans la save network
                    console.log("update", update, saveAllFormData.networks[page.network], mergeUpdates(JSON.parse(JSON.stringify(update)), saveAllFormData.networks[page.network]))
                    originalDraft.saveAllFormData.networks[page.network] = saveAllFormData.networks[page.network] ? mergeUpdates(JSON.parse(JSON.stringify(update)), saveAllFormData.networks[page.network]) : update
                } else {
                    //si error de la page ou du reseau alors mettre tout les changements dans la save de la page
                    originalDraft.saveAllFormData.pages[page.id] = mergeUpdates(mergeUpdates(JSON.parse(JSON.stringify(update)), saveAllFormData.networks[page.network] || {}), saveAllFormData.pages[page.id] || {})
                }
            }
        }
        originalDraft.files = files
        newDraft = JSON.stringify(originalDraft)
        console.log("originalDraft", originalDraft)

    }

    const log = generateLog({
        title: 'dash.posts.log.updated-posts',
        newFormData: saveAllFormData,
        lastFormData: dataEditPost.draft.saveAllFormData,
        newIdUsersAssigned: snapshot.idUsersAssigned,
        lastIdUsersAssigned: dataEditPost.draft.idUsersAssigned,
        newRequiredValidation: snapshot.requiredValidation,
        lastRequiredValidation: dataEditPost.draft.requiredValidation
    })


    for (const [pageId, formData] of Object.entries(dataFormRequest)) {
        if (formData.hasOwnProperty('error')) {
            delete dataFormRequest[pageId];
        } else {
            const post = dataEditPost.posts.find(post => post.page == pageId);
            if (post) {
                //update post for page
                formData.postId = post.postId
                formData.id = post._id
                formData.log = JSON.stringify([JSON.stringify(log)]) //JSON.stringify([JSON.stringify(log), ...this.logs])
                if (newDraft) formData["draft"] = newDraft

                formData.data.editPostAlreadyPosted = true
                delete formData.data.storification // empecher la mise a jour de la storification via la mise a jour de post
            } else {
                delete dataFormRequest[pageId];
            }
        }
    }

    return editAllPosts(dataFormRequest).then((response) => {
        addLog(currentUser, `${mode}All-post`, {
            payload: {
                ...dataFormRequest,
                draft,
                front_version,
            },
            response: response
        });
        Object.entries(response.body.news).forEach(([pageId, news]) => {
            console.log("updateALL post loop", response.body.news)
            let formData = dataFormRequest[pageId]
            const page = pagesSelected.find(page => page.id == pageId) || allPages[pageId]

            requestGroup.push({
                pageId,
                start: async (onSuccess, onprogress, onError) => {
                    try {
                        console.log("updateAll eventsource")
                        const eventSource = events(news.id);

                        eventSource.onopen = () => {
                            console.log('updateAll event opened: ', news.id);
                        }
                        eventSource.onerror = (error) => {
                            console.log('updateAll event onerror', error, "postId: ", news.id);
                            onError();
                            eventSource.close();
                        }
                        eventSource.onmessage = (event) => {
                            console.log('updateAll onmessage (', news, ') : ', event)
                            if (event.data === 'keep alive') return;
                            const data = JSON.parse(event.data);
                            if (data.progress.aborted) return;
                            if (data.progress.success) {
                                eventSource.close();
                                onSuccess(t('dash.post-creator.request.post-updated'));
                                EventBus.$emit('planner/refresh', 500);
                            } else if (data.progress.success === false) {
                                eventSource.close();
                                onError(t('dash.post-creator.request.post-update-failed'));
                                EventBus.$emit('planner/refresh', 500);
                            } else {
                                onprogress(data.progress.message);
                            }
                        }
                    } catch (error) {
                        onError(error.message);
                        return { response: null, formData: { ...formData, pageId } }
                    }
                },
                params: {
                    picture: formData.data.enclosure || page.avatar,
                    title: `<span class="pink">${page.name.charAt(0).toUpperCase() + page.name.slice(1)}</span>: ${formData.data.title || "post medias"}`,
                    loadingText: t('dash.post-creator.request.updating')
                }
            });
            (deps.publishRequestGroup || defaultPublishRequestGroup)(requestGroup, async (responses) => { EventBus.$emit('planner/refresh', 0) });

        });
    })
        .catch((err) => {
            console.log("updateAllPosts error: ", err)
        })
}

// Single entry point - dispatches to the branch matching `mode`, exactly like the original
// switch(this.mode) did. Returns the underlying createAllPosts/editAllPosts promise chain (never
// rejects - each branch already swallows its own errors into a .catch(console.log), same
// pre-existing behavior as before this extraction, not something introduced here) so a caller that
// needs to know "the publish call was dispatched" (see PostCreator.vue's deferred-publish path) can
// await it; the live/fast path doesn't await it either, same as before.
export function runPublishBranch(mode, snapshot, deps) {
    clarityEvent(`post_submit:${mode}`)
    switch (mode) {
        case 'create':
        case 'duplicate':
            return publishCreateOrDuplicate(snapshot, deps)
        case 'edit':
            return publishEdit(snapshot, deps)
        case 'draft':
            return publishDraft(snapshot, deps)
        case 'update':
            return publishUpdate(snapshot, deps)
        default:
            return Promise.resolve()
    }
}
